import React from "react";
import { Alert, Button, Form } from "@payping/web-components";
import { useAppTranslation } from "@/utils/useAppTranslation";
import { buildUrl } from "@payping/utilities-shared/url";
import { URLS } from "@payping/utilities-shared/consts";
import { IBANFormField } from "./IBANFormField";
import { InformationSection } from "./InformationSection";

interface IBANEntry {
  title: string;
  iban: string;
}

interface MultipleIBANFormProps {
  form: any;
  onFinish: (values: { ibans: IBANEntry[] }) => void;
  onFormValuesChange: (changedValues: any, allValues: any) => void;
}

export const MultipleIBANForm: React.FC<MultipleIBANFormProps> = ({ form, onFinish, onFormValuesChange }) => {
  const { t } = useAppTranslation();

  const handleFinish = async (values: any) => {
    const ibans: IBANEntry[] = [
      { title: values.title1 || "", iban: values.iban1 || "" },
      { title: values.title2 || "", iban: values.iban2 || "" },
      { title: values.title3 || "", iban: values.iban3 || "" },
    ].filter((entry) => entry.iban && entry.iban.trim() !== "");

    // Prevent submitting with empty IBANs array
    if (ibans.length === 0) {
      return;
    }

    onFinish({ ibans });
  };

  const handleFormValuesChange = (changedValues: any, allValues: any) => {
    onFormValuesChange(changedValues, allValues);
    // Re-validate fields that come after the changed field
    const changedField = Object.keys(changedValues)[0];
    const fieldOrder = ["iban1", "iban2", "iban3"];
    const currentIndex = fieldOrder.indexOf(changedField);

    if (currentIndex !== -1) {
      // Only re-validate fields after this one
      const fieldsAfter = fieldOrder.slice(currentIndex + 1);
      fieldsAfter.forEach((field) => {
        if (form.getFieldValue(field)) {
          form.validateFields([field]);
        }
      });
    }
  };

  return (
    <>
      <Alert message={t("profile.bank.atLeastOneIbanRequired")} type="info" showIcon className="tw-mt-4" />

      <div className="tw-py-8">
        <div className="tw-flex tw-items-center tw-justify-between tw-pb-3">
          <div className="tw-text-gray-900 tw-text-right tw-text-sm tw-font-normal tw-leading-5">
            {t("profile.bank.ibanNumber")}
          </div>
          <Button
            size="small"
            variant="outlined"
            onClick={() => window.open(buildUrl(URLS.BLOG_URL, "sheba").toString(), "_blank")}
          >
            <div className="tw-text-gray-800 tw-text-sm tw-font-medium tw-leading-5">{t("profile.bank.ibanGuide")}</div>
          </Button>
        </div>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleFinish}
          onValuesChange={handleFormValuesChange}
          className="tw-self-stretch"
        >
          <div className="tw-flex tw-flex-col tw-items-start tw-justify-start tw-gap-4 tw-self-stretch">
            <IBANFormField
              form={form}
              ibanFieldName="iban1"
              titleFieldName="title1"
              ibanPlaceholder={t("profile.bank.ibanPlaceholder1")}
              titlePlaceholder={t("profile.bank.titlePlaceholder1")}
              isRequired={true}
            />

            <IBANFormField
              form={form}
              ibanFieldName="iban2"
              titleFieldName="title2"
              ibanPlaceholder={t("profile.bank.ibanPlaceholder2")}
              titlePlaceholder={t("profile.bank.titlePlaceholder2")}
            />

            <IBANFormField
              form={form}
              ibanFieldName="iban3"
              titleFieldName="title3"
              ibanPlaceholder={t("profile.bank.ibanPlaceholder3")}
              titlePlaceholder={t("profile.bank.titlePlaceholder3")}
            />
          </div>
        </Form>
      </div>

      <InformationSection />
    </>
  );
};
